/** @jsx jsx */
import React, { useState, useEffect } from "react"
import { jsx } from "@emotion/react"
import styled from "@emotion/styled"
import { colorScheme, fontScheme } from "../../helpers/styleSettings"

interface ExternalLinkModalProps {
  href: string;
  children: React.ReactNode;
  className?: string;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`

const ModalContent = styled.div`
  background: ${colorScheme.dark};
  padding: 2em;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  color: ${colorScheme.supportDark};
  font-family: ${fontScheme.text};

  h2 {
    font-family: ${fontScheme.headings};
    font-size: 1.5em;
    margin-bottom: 0.5em;
    color: ${colorScheme.supportDark};
  }

  p {
    margin-bottom: 1.5em;
    line-height: 1.5;
    font-size: 0.95em;
  }

  .buttons {
    display: flex;
    gap: 1em;
    justify-content: center;
  }

  .button {
    padding: 0.75em 1.5em;
    border: none;
    border-radius: 25px;
    font-family: ${fontScheme.headings};
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;

    &:hover {
      opacity: 0.9;
    }
  }

  .button-stay {
    background: transparent;
    color: ${colorScheme.supportDark};
    border: 2px solid ${colorScheme.supportDark};
  }

  .button-go {
    background: ${colorScheme.supportDark};
    color: ${colorScheme.dark};
  }

  .site-name {
    font-weight: 700;
  }
`

const ExternalLinkModal: React.FC<ExternalLinkModalProps> = ({ href, children, className }) => {
  const [showModal, setShowModal] = useState(false)
  const [externalUrl, setExternalUrl] = useState("")

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [showModal])

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setExternalUrl(href)
    setShowModal(true)
  }

  const handleStay = () => {
    setShowModal(false)
  }

  const handleGo = () => {
    window.location.href = href
  }

  const getDomain = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, "")
    } catch {
      return url
    }
  }

  const domain = getDomain(externalUrl)

  if (!showModal) {
    return (
      <a href={href} onClick={handleClick} className={className}>
        {children}
      </a>
    )
  }

  return (
    <>
      <a href={href} onClick={handleClick} className={className}>
        {children}
      </a>
      <ModalOverlay onClick={handleStay}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <h2>You are about to leave abitago.se</h2>
          <p>
            You are leaving abitago.se and entering an external site. 
            A Bit Ago is not responsible for the content or privacy practices of external sites.
          </p>
          <div className="buttons">
            <button className="button button-stay" onClick={handleStay}>
              Stay on abitago.se
            </button>
            <button className="button button-go" onClick={handleGo}>
              Go to {domain}
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    </>
  )
}

export default ExternalLinkModal
